'use client';

import { useEffect } from 'react';
import { useTranslation } from '@/contexts/LanguageContext';
import { Button } from '@/components/ui/button';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Languages } from 'lucide-react';

export function LanguageSwitcher() {
    const { language, setLanguage } = useTranslation();

    // Sync Google Translate on mount and when language changes
    useEffect(() => {
        const syncGoogleTranslate = () => {
            const googleTranslateElement = document.querySelector('.goog-te-combo') as HTMLSelectElement;
            if (googleTranslateElement && googleTranslateElement.value !== language) {
                googleTranslateElement.value = language;
                googleTranslateElement.dispatchEvent(new Event('change'));
            }
        };

        // Delay a bit to ensure Google Translate is loaded
        const timer = setTimeout(syncGoogleTranslate, 1000);
        return () => clearTimeout(timer);
    }, [language]);

    const handleLanguageChange = (lang: 'en' | 'es' | 'fr' | 'de') => {
        setLanguage(lang);
        localStorage.setItem('language', lang);

        if (lang === 'en') {
            // Clear Google Translate cookies to revert to original language
            document.cookie = 'googtrans=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
            document.cookie = `googtrans=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=${window.location.hostname};`;
            // Also try with a dot prefix for domain
            document.cookie = `googtrans=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=.${window.location.hostname};`;
        } else {
            // Set cookie for Google Translate
            document.cookie = `googtrans=/en/${lang}; path=/`;
            document.cookie = `googtrans=/en/${lang}; path=/; domain=${window.location.hostname}`;
        }

        // Refresh to apply Google Translate
        window.location.reload();
    };

    return (
        <DropdownMenu>
            <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" className="h-9 w-9">
                    <Languages className="h-4 w-4" />
                    <span className="sr-only">Switch language</span>
                </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => handleLanguageChange('en')} className={language === 'en' ? 'bg-accent' : ''}>
                    English
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => handleLanguageChange('es')} className={language === 'es' ? 'bg-accent' : ''}>
                    Español
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => handleLanguageChange('fr')} className={language === 'fr' ? 'bg-accent' : ''}>
                    Français
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => handleLanguageChange('de')} className={language === 'de' ? 'bg-accent' : ''}>
                    Deutsch
                </DropdownMenuItem>
            </DropdownMenuContent>
        </DropdownMenu>
    );
}
